import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { FormlyModule } from '@ngx-formly/core';
import { MiscService } from '@app/core/services/misc.service';
import { DynamicIconComponent } from "@app/shared/components/dynamic-icon";
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@env/environment';

export interface Medium {
  fileId: string;
  mediaType: string;
  originalname: string;
}

export interface SignatureModel {
  media: Medium[];
  selectedFiles: File[];
}

@Component({
  selector: 'app-formly-field-signature',
  templateUrl: './formly-field-signature.component.html',
  styleUrls: ['./formly-field-signature.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    FormlyModule,
    TranslateModule,
    ReactiveFormsModule,
    DynamicIconComponent
  ],
})
export class FormlyFieldSignatureComponent extends FormlyFieldAnyComponent implements OnInit, AfterViewInit, OnDestroy {
  
  @ViewChild('signatureCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('readonlyCanvas', { static: false }) readonlyCanvasRef!: ElementRef<HTMLCanvasElement>;
  
  public signatureModel: SignatureModel = {
    media: [],
    selectedFiles: [],
  };

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  
  private subsink = new SubSink();
  private readonly activeObjectUrls = new Set<string>();

  public readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  constructor(
    private miscService: MiscService,
    private changeDetectorRef: ChangeDetectorRef,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {
    super();
  }

  ngOnInit(): void {
    // // Subscribe to formControl value changes to update localValue
    // this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
    //   this.updateLocalValue(value);
    // });

    // // Initialize with current value
    this.updateLocalValue(this.formControl.value);
  }

  async ngAfterViewInit(): Promise<void> {
    // Use longer timeout to ensure the DOM is fully laid out with proper dimensions
    setTimeout(async () => {
      if (this.canvasRef) {
        this.initializeCanvas();
        // Load signature if data exists (in case updateLocalValue was called before canvas was ready)
        if (this.hasSignature()) {
          await this.loadSignatureFromModel();
        }
      }
      if (this.readonlyCanvasRef && this.hasSignature()) {
        await this.renderReadonlySignature();
      }
    }, 100); // Increased timeout to allow DOM layout
  }

  private initializeCanvas(): void {
    this.canvas = this.canvasRef.nativeElement;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }
    this.ctx = ctx;

    // Set canvas size - must match the actual display size
    const container = this.canvas.parentElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      // Set internal canvas dimensions to match display size
      this.canvas.width = rect.width || 400;
      this.canvas.height = rect.height || this.field?.props?.['canvasHeight'] || 200;
    }

    // Fill with white background
    this.ctx.fillStyle = 'white';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Set drawing style
    this.ctx.strokeStyle = this.field?.props?.['strokeColor'] || '#000000';
    this.ctx.lineWidth = this.field?.props?.['lineWidth'] || 3;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    // Setup event listeners
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Mouse events
    this.canvas.addEventListener('mousedown', this.startDrawing.bind(this));
    this.canvas.addEventListener('mousemove', this.draw.bind(this));
    this.canvas.addEventListener('mouseup', this.stopDrawing.bind(this));
    this.canvas.addEventListener('mouseout', this.stopDrawing.bind(this));

    // Touch events
    this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this));
    this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this));
    this.canvas.addEventListener('touchend', this.stopDrawing.bind(this));
    this.canvas.addEventListener('touchcancel', this.stopDrawing.bind(this));
  }

  private startDrawing(e: MouseEvent): void {
    if (this.isReadonly()) return;
    
    this.isDrawing = true;
    const rect = this.canvas.getBoundingClientRect();
    // Scale coordinates to match canvas internal dimensions
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    this.lastX = (e.clientX - rect.left) * scaleX;
    this.lastY = (e.clientY - rect.top) * scaleY;
  }

  private draw(e: MouseEvent): void {
    if (!this.isDrawing || this.isReadonly()) return;

    const rect = this.canvas.getBoundingClientRect();
    // Scale coordinates to match canvas internal dimensions
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const currentX = (e.clientX - rect.left) * scaleX;
    const currentY = (e.clientY - rect.top) * scaleY;

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(currentX, currentY);
    this.ctx.stroke();

    this.lastX = currentX;
    this.lastY = currentY;
  }

  private stopDrawing(): void {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.saveSignature();
    }
  }

  private handleTouchStart(e: TouchEvent): void {
    if (this.isReadonly()) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const rect = this.canvas.getBoundingClientRect();
    
    // Scale coordinates to match canvas internal dimensions
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    
    this.isDrawing = true;
    this.lastX = (touch.clientX - rect.left) * scaleX;
    this.lastY = (touch.clientY - rect.top) * scaleY;
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.isDrawing || this.isReadonly()) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const rect = this.canvas.getBoundingClientRect();
    
    // Scale coordinates to match canvas internal dimensions
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const currentX = (touch.clientX - rect.left) * scaleX;
    const currentY = (touch.clientY - rect.top) * scaleY;

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(currentX, currentY);
    this.ctx.stroke();

    this.lastX = currentX;
    this.lastY = currentY;
  }

  private async saveSignature(): Promise<void> {
    // Convert canvas to blob
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        if (blob) {
          // Create a File object from the blob
          const timestamp = new Date().getTime();
          const file = new File([blob], `signature_${timestamp}.png`, { 
            type: 'image/png',
            lastModified: timestamp
          });
          
          // Clear media and set the new signature file
          this.signatureModel.media = [];
          this.signatureModel.selectedFiles = [file];
          
          this.formControl.setValue(this.signatureModel);
          this.formControl.markAsDirty();
          this.onChange(this.signatureModel);
        }
        resolve();
      }, 'image/png');
    });
  }

  private async updateLocalValue(modelValue: any): Promise<void> {
    if (modelValue && typeof modelValue === 'object') {
      this.signatureModel.media = modelValue.media || [];
      this.signatureModel.selectedFiles = modelValue.selectedFiles || [];
    } else {
      this.signatureModel = { media: [], selectedFiles: [] };
    }
    
    // Load signature on canvas if available
    if (this.canvas && this.ctx) {
      await this.loadSignatureFromModel();
    }
    
    // Render readonly signature if in readonly mode
    if (this.readonlyCanvasRef && this.hasSignature()) {
      await this.renderReadonlySignature();
    }
    
    this.onChange(this.signatureModel);
    this.changeDetectorRef.markForCheck();
  }

  private async loadSignatureFromModel(): Promise<void> {
    // Ensure canvas is ready
    if (!this.ctx || !this.canvas) {
      return;
    }

    // Clear canvas first
    this.ctx.fillStyle = 'white';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Load from existing media (uploaded files)
    if (this.signatureModel.media.length > 0) {
      const medium = this.signatureModel.media[0];
      const imageUrl = `${environment.apiUrl}/medias/${medium.fileId}/download`;
      
      // Use HttpClient to fetch with authentication
      try {
        const response = await this.http.get(imageUrl, { observe: 'response', responseType: 'blob' }).toPromise();
        if (response && response.body) {
          const objectUrl = this.registerObjectUrl(URL.createObjectURL(response.body));
          const img = new Image();
          img.onload = () => {
            // Calculate scaling to fit canvas while maintaining aspect ratio
            const scale = Math.min(
              this.canvas.width / img.width,
              this.canvas.height / img.height
            );
            
            const x = (this.canvas.width - img.width * scale) / 2;
            const y = (this.canvas.height - img.height * scale) / 2;
            
            this.ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
            this.releaseObjectUrl(objectUrl);
            this.changeDetectorRef.markForCheck();
          };
          img.onerror = () => {
            console.error('Failed to load signature image');
            this.releaseObjectUrl(objectUrl);
          };
          img.src = objectUrl;
        }
      } catch (err) {
        console.error('Error fetching signature image:', err);
      }
    } else if (this.signatureModel.selectedFiles.length > 0) {
      // Load from selected file (newly drawn signature)
      const file = this.signatureModel.selectedFiles[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          // Calculate scaling to fit canvas while maintaining aspect ratio
          const scale = Math.min(
            this.canvas.width / img.width,
            this.canvas.height / img.height
          );
          
          const x = (this.canvas.width - img.width * scale) / 2;
          const y = (this.canvas.height - img.height * scale) / 2;
          
          this.ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          this.changeDetectorRef.markForCheck();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  public clearSignature(): void {
    if (this.isReadonly()) return;
    
    // Fill with white background
    this.ctx.fillStyle = 'white';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    this.signatureModel.selectedFiles = [];
    this.signatureModel.media = [];
    
    this.formControl.setValue(null);
    this.formControl.markAsDirty();
    this.onChange(null);
    this.changeDetectorRef.markForCheck();
  }

  public onFileSelect(event: any): void {
    if (this.isReadonly()) return;
    
    const file = event.target?.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.miscService.showErrorToast('ERRORS.FILE_TOO_LARGE', true, { 
        filename: file.name, 
        maxFileSize: `${this.MAX_FILE_SIZE / 1024 / 1024} MB` 
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.miscService.showErrorToast('ERRORS.UNSUPPORTED_FORMAT', true, { filename: file.name });
      return;
    }

    // Read the file and load it onto the canvas
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        // Fill with white background first
        this.ctx.fillStyle = 'white';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Calculate scaling to fit canvas while maintaining aspect ratio
        const scale = Math.min(
          this.canvas.width / img.width,
          this.canvas.height / img.height
        );
        
        const x = (this.canvas.width - img.width * scale) / 2;
        const y = (this.canvas.height - img.height * scale) / 2;
        
        this.ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Clear any existing signatures and add the new file
        this.signatureModel.media = [];
        this.signatureModel.selectedFiles = [file];
        this.formControl.setValue(this.signatureModel);
        this.formControl.markAsDirty();
        this.onChange(this.signatureModel);
        this.changeDetectorRef.markForCheck();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);

    // Clear the input
    event.target.value = '';
  }

  public hasSignature(): boolean {
    return this.signatureModel.media.length > 0 || this.signatureModel.selectedFiles.length > 0;
  }

  public isReadonly(): boolean {
    return !!(
      this.options.formState.disabled ||
      this.field.props?.['disabled'] ||
      this.field.props?.['readonly']
    );
  }

  private async renderReadonlySignature(): Promise<void> {
    if (!this.readonlyCanvasRef) {
      return;
    }

    const canvas = this.readonlyCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      return;
    }

    // Set canvas size
    const container = canvas.parentElement;
    
    if (!container) {
      return;
    }

    const containerWidth = container.clientWidth;

    // If container width is 0, wait and retry
    if (containerWidth === 0) {
      setTimeout(() => this.renderReadonlySignature(), 100);
      return;
    }

    canvas.width = containerWidth;
    canvas.height = 200;

    // Set white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    try {
      // Load from media or selectedFiles
      if (this.signatureModel.media.length > 0) {
        // Load from uploaded media using authenticated request
        const medium = this.signatureModel.media[0];
        const imageUrl = `${environment.apiUrl}/medias/${medium.fileId}/download`;
        
        const response = await this.http.get(imageUrl, { observe: 'response', responseType: 'blob' }).toPromise();
        if (response && response.body) {
          const objectUrl = this.registerObjectUrl(URL.createObjectURL(response.body));
          const img = new Image();
          img.onload = () => {
            // Calculate scaling to fit canvas while maintaining aspect ratio
            const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
            const x = (canvas.width - img.width * scale) / 2;
            const y = (canvas.height - img.height * scale) / 2;
            
            ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
            this.releaseObjectUrl(objectUrl);
          };
          img.onerror = () => {
            console.error('Failed to load readonly signature image');
            this.releaseObjectUrl(objectUrl);
          };
          img.src = objectUrl;
        }
      } else if (this.signatureModel.selectedFiles.length > 0) {
        // Load from selected file
        const file = this.signatureModel.selectedFiles[0];
        const objectUrl = this.registerObjectUrl(URL.createObjectURL(file));
        const img = new Image();
        img.onload = () => {
          // Calculate scaling to fit canvas while maintaining aspect ratio
          const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
          const x = (canvas.width - img.width * scale) / 2;
          const y = (canvas.height - img.height * scale) / 2;
          
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          this.releaseObjectUrl(objectUrl);
        };
        img.onerror = () => {
          this.releaseObjectUrl(objectUrl);
        };
        img.src = objectUrl;
      }
    } catch (error) {
      console.error('Error rendering signature:', error);
    }
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
    this.releaseAllObjectUrls();
  }

  private registerObjectUrl(objectUrl: string): string {
    this.activeObjectUrls.add(objectUrl);
    return objectUrl;
  }

  private releaseObjectUrl(objectUrl: string): void {
    if (!this.activeObjectUrls.has(objectUrl)) {
      return;
    }

    URL.revokeObjectURL(objectUrl);
    this.activeObjectUrls.delete(objectUrl);
  }

  private releaseAllObjectUrls(): void {
    for (const objectUrl of this.activeObjectUrls) {
      URL.revokeObjectURL(objectUrl);
    }
    this.activeObjectUrls.clear();
  }
}
